import {
  ModelContextSettings,
  type ModelContextPolicy,
} from "./model-context-settings";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Plus, Pencil, Loader2, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { apiDelete, apiGet, apiPatch } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PersonalProviderDialog } from "./ai-chat";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
export function PersonalProviders() {
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const [editing, setEditing] = useState<string | null | undefined>();
  const [removing, setRemoving] = useState<{ kind: "providers" | "models"; id: string; name: string } | null>(null);
  const remove = useMutation({
    mutationFn: (item: { kind: "providers" | "models"; id: string }) => apiDelete(`/ai/${item.kind}/${item.id}`),
    onSuccess: () => { setRemoving(null); void client.invalidateQueries({ queryKey: ["ai-providers"] }); void client.invalidateQueries({ queryKey: ["ai-project-models"] }); void client.invalidateQueries({ queryKey: ["assistant-automations"] }); },
  });
  const providers = useQuery({
    queryKey: ["ai-providers"],
    queryFn: () =>
      apiGet<{
        connections: {
          id: string;
          label: string;
          enabled: boolean;
          keyHint: string;
          modelConfigs: {
            id: string;
            modelId: string;
            enabled: boolean;
            capabilities?: { contextPolicy?: ModelContextPolicy };
          }[];
        }[];
      }>("/ai/providers"),
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiPatch(`/ai/providers/${id}`, { enabled }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["ai-providers"] });
      void client.invalidateQueries({ queryKey: ["ai-project-models"] });
    },
  });
  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground-secondary">{fr ? "Vous n’avez pas encore de clé IA ? " : "Don’t have an AI key yet? "}<Link to="/ai/setup" className="text-primary underline underline-offset-2">{fr ? "Configurer un modèle gratuit" : "Set up a free model"}</Link></p>
      {providers.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
      {providers.data?.connections.map((c) => (
        <div
          key={c.id}
          className="rounded-xl border border-white/10 bg-white/[0.03] p-4"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Bot className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{c.label}</p>
              <p className="text-xs text-foreground-muted">•••• {c.keyHint}</p>
            </div>
            <Switch
              aria-label={fr ? "Activer le fournisseur" : "Enable provider"}
              checked={c.enabled}
              disabled={toggle.isPending}
              onCheckedChange={(enabled) =>
                toggle.mutate({ id: c.id, enabled })
              }
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label={fr ? "Modifier le fournisseur" : "Edit provider"}
              onClick={() => setEditing(c.id)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={fr ? `Supprimer le fournisseur ${c.label}` : `Remove provider ${c.label}`} onClick={() => { remove.reset(); setRemoving({ kind: "providers", id: c.id, name: c.label }); }}><Trash2 className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {c.modelConfigs
              .map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2 py-1 text-xs max-w-full text-foreground-secondary"
                >
                  <span className="truncate">{m.modelId}</span>{!m.enabled && <span className="text-foreground-muted">{fr ? "Désactivé" : "Disabled"}</span>}
                  <ModelContextSettings
                    modelId={m.id}
                    name={m.modelId}
                    policy={m.capabilities?.contextPolicy}
                  />
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" aria-label={fr ? `Supprimer le modèle ${m.modelId}` : `Remove model ${m.modelId}`} onClick={() => { remove.reset(); setRemoving({ kind: "models", id: m.id, name: m.modelId }); }}><Trash2 className="h-3 w-3" /></Button>
                </span>
              ))}
          </div>
        </div>
      ))}
      {!providers.isLoading && !providers.data?.connections.length && (
        <p className="rounded-xl border border-dashed border-white/15 px-4 py-6 text-center text-sm text-foreground-muted">
          {fr
            ? "Ajoutez votre premier fournisseur pour utiliser le Chat AI."
            : "Add your first provider to use Chat AI."}
        </p>
      )}
      {(providers.error || toggle.error) && (
        <p role="alert" className="text-sm text-destructive">
          {(providers.error || toggle.error)?.message}
        </p>
      )}
      <Button variant="outline" onClick={() => setEditing(null)}>
        <Plus className="mr-2 h-4 w-4" />
        {fr ? "Ajouter un fournisseur" : "Add provider"}
      </Button>
      <PersonalProviderDialog
        open={editing !== undefined}
        connectionId={editing ?? undefined}
        onClose={() => setEditing(undefined)}
      />
      <Dialog open={!!removing} onOpenChange={(open) => { if (!open && !remove.isPending) setRemoving(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{fr ? "Supprimer" : "Remove"} {removing?.name}</DialogTitle><DialogDescription>{fr ? "Ce modèle ne sera plus disponible dans vos chats ni dans les projets où vous l’avez partagé. Les automatisations qui l’utilisent seront mises en pause. Vos conversations et leur historique sont conservés." : "The model will no longer be available in your chats or projects where you shared it. Automations using it will be paused. Your conversations and history are kept."} {removing?.kind === "providers" && (fr ? "La clé du fournisseur sera également supprimée." : "The provider key will also be deleted.")}</DialogDescription></DialogHeader>
          {remove.error && <p role="alert" className="text-sm text-destructive">{remove.error.message}</p>}
          <DialogFooter><Button variant="outline" disabled={remove.isPending} onClick={() => setRemoving(null)}>{fr ? "Annuler" : "Cancel"}</Button><Button variant="destructive" disabled={remove.isPending} onClick={() => removing && remove.mutate(removing)}>{remove.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{fr ? "Supprimer" : "Remove"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
